import { BDDialog } from '@brightdrop/bd-ui';
import { Link, Typography } from '@mui/material';
import { Trans, useTranslation } from 'react-i18next';
import { useGmFleetRebrandLegal } from '~/common/hooks/useGmFleetRebrandLegal';
import { useOnStarSupportEmail } from '~/common/hooks/useOnStarFleetIntelligenceSupportEmail';
import useStyles from './Modal.styles';

const TRANSLATION_KEYS = {
  title: 'common:termsConditions.title',
  preamble: 'quickPay:termsAndConditions.preamble',
  items: 'quickPay:termsAndConditions.items',
} as const;

type TermsAndConditionsModalProps = {
  open: boolean;
  onClose: () => void;
};

const TermsAndConditionsContent = () => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const supportEmail = useOnStarSupportEmail();
  const { privacyStatementUrl } = useGmFleetRebrandLegal();
  const items: string[] = t(TRANSLATION_KEYS.items, { returnObjects: true });

  const resourceLinks = {
    privacyLink: (
      <Link
        href={privacyStatementUrl}
        target="_blank"
        rel="noopener noreferrer"
        className={classes.boldLink}
      />
    ),
    emailLink: (
      <Link href={`mailto:${supportEmail}`} className={classes.boldLink} />
    ),
  };

  return (
    <Typography
      component="div"
      variant="body_regular"
      color="text.secondary"
      data-testid="terms-and-conditions-modal"
    >
      <p className={classes.preamble}>{t(TRANSLATION_KEYS.preamble)}</p>
      <ul className={classes.list}>
        {items.map((item, index) => (
          <li key={item}>
            <Trans
              i18nKey={`quickPay:termsAndConditions.items.${index}`}
              components={resourceLinks}
              values={{ supportEmail }}
            />
          </li>
        ))}
      </ul>
    </Typography>
  );
};

const TermsAndConditionsModal = ({
  open,
  onClose,
}: TermsAndConditionsModalProps) => {
  const { t } = useTranslation();

  return (
    <BDDialog
      openModal={open}
      title={t(TRANSLATION_KEYS.title)}
      onCloseModal={onClose}
      component={() => <TermsAndConditionsContent />}
    />
  );
};

export default TermsAndConditionsModal;
